import { createContext, Dispatch, ReactElement, SetStateAction, useMemo, useState } from "react"

// ** Third Party Components
import axios from "axios"
import { parseCookies } from "nookies"
import { useParams } from "react-router-dom"
import { useQuery } from "@tanstack/react-query"

// ** Routes
import { PROJECT_BY_ID_ENDPOINT, SERVER_URL } from "@pma/utils/api-routes"

// ** Variables
import { ACCESS_TOKEN, ADMIN_ROLE_ID } from "@pma/utils/constants"
import { useAuth } from "@pma/stores/auth-store"

interface UserRoleDetails {
  constructor: boolean
  supervisor: boolean
  fin_manager: boolean
  designer: boolean
  view_tasks: boolean
  edit_tasks: boolean
}

interface UserRoleContextParams {
  userRoleDetails: UserRoleDetails
  projectDetails: any
  projectDefaultContractor: any
  setCurrentProjectId: Dispatch<SetStateAction<any>>
}

const defaultContext = {
  userRoleDetails: {
    constructor: false,
    supervisor: false,
    fin_manager: false,
    designer: false,
    view_tasks: false,
    edit_tasks: false,
  },
  projectDetails: null,
  projectDefaultContractor: null,
  setCurrentProjectId: () => {},
}

export const UserRoleContext = createContext<UserRoleContextParams>(defaultContext)

export const UserRoleProvider = ({ children }: { children: ReactElement }) => {
  const { authConfig } = useAuth()
  const projectId = useParams().projectId
  const accessToken = parseCookies()[ACCESS_TOKEN]

  const [currentProjectId, setCurrentProjectId] = useState<any>(null)

  const getProjectRoles = async () => {
    const id = projectId || currentProjectId
    const response = await axios.get(SERVER_URL + `/projects/roles/get?project_id=${id}`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    })
    return response.data
  }

  const { data: projectUserBaseOnRole } = useQuery({
    queryKey: ["user-role-details", projectId, currentProjectId],
    queryFn: getProjectRoles,
    enabled: !!projectId || !!currentProjectId,
  })

  const getProject = async () => {
    const response = await axios.get(SERVER_URL + PROJECT_BY_ID_ENDPOINT + `/${projectId}`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    })
    return response.data
  }

  const { data: projectDetails } = useQuery({
    queryKey: ["get-project", projectId],
    queryFn: getProject,
    enabled: !!projectId,
  })

  const userRoleDetails: UserRoleDetails = useMemo(() => {
    let userRole: UserRoleDetails = {
      constructor: false,
      supervisor: false,
      fin_manager: false,
      designer: false,
      view_tasks: false,
      edit_tasks: false,
    }

    let userInRole = false
    projectUserBaseOnRole?.forEach((user: any) => {
      if (user.user_id === authConfig.id) {
        userInRole = true
        const isAdmin = authConfig?.role?.id === ADMIN_ROLE_ID
        if (userRole.constructor == false) userRole.constructor = isAdmin ? true : user.contractor
        if (userRole.supervisor == false) userRole.supervisor = isAdmin ? true : user.supervisor
        if (userRole.fin_manager == false) userRole.fin_manager = isAdmin ? true : user.financial
        if (userRole.designer == false) userRole.designer = isAdmin ? true : user.designer
        if (userRole.view_tasks == false) userRole.view_tasks = isAdmin ? true : user.view_tasks
        if (userRole.edit_tasks == false) userRole.edit_tasks = isAdmin ? true : user.edit_tasks
      }
    })

    authConfig?.companies_of_user?.forEach((org: any) => {
      if (org.id == authConfig?.last_organization_id && org?.role?.id == ADMIN_ROLE_ID) {
        userRole.constructor = true
        userRole.supervisor = true
        userRole.fin_manager = true
        userRole.designer = true
        userRole.view_tasks = true
        userRole.edit_tasks = true
      }
    })

    return userRole
  }, [projectUserBaseOnRole, authConfig])

  const projectDefaultContractor = useMemo(() => {
    let projectContractor = null

    if (projectDetails && projectDetails?._default_contractor)
      projectContractor = projectDetails?._default_contractor

    return projectContractor
  }, [projectDetails])

  return (
    <UserRoleContext.Provider
      value={{
        userRoleDetails,
        projectDefaultContractor,
        projectDetails,
        setCurrentProjectId,
      }}
    >
      {children}
    </UserRoleContext.Provider>
  )
}
